import Field from 'components/forms/field/Field';
import { useDataStreamConfig } from 'dashboard-engine/hooks/useDataStreamConfig';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import EditorSelect from 'ui/editor/components/EditorSelect';
import { useDatasetContext } from 'ui/editor/dataStream/contexts/DatasetContext';
import { StepTitleAndControls } from 'ui/editor/dataStream/TileEditor/steps/StepTitleAndControls';
import { usePluginImportConfigEditorContext } from './PluginImportConfigEditorContext';

type PluginImportConfigMappingFormData = {
    sourceIdColumn?: string;
    sourceNameColumn?: string;
    sourceType?: {
        mode: 'fixed' | 'column';
        value?: string;
        column?: string;
    };
    columnMapping: { key: string; value: string }[];
};

export const PluginImportConfigMappingStep = () => {
    const { sourceIdColumn, sourceNameColumn, sourceType, columnMapping, setConfig } =
        usePluginImportConfigEditorContext();

    const { config } = useDatasetContext();
    const { data } = useDataStreamConfig(config, { keepPreviousData: true });

    const formMethods = useForm<PluginImportConfigMappingFormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            sourceIdColumn,
            sourceNameColumn,
            sourceType: {
                ...sourceType,
                mode: sourceType?.mode ?? 'fixed'
            },
            columnMapping: Object.entries(columnMapping ?? {}).map(([key, value]) => ({
                key,
                value
            }))
        }
    });

    const onChange = useCallback(
        (formData: PluginImportConfigMappingFormData) => {
            setConfig({
                tileConfig: config,
                sourceIdColumn: formData.sourceIdColumn,
                sourceNameColumn: formData.sourceNameColumn,
                sourceType: formData.sourceType,
                columnMapping: Object.fromEntries(
                    formData.columnMapping
                        .filter((m) => m.key && m.value) // Ignore empty mappings
                        .map((item: { key: string; value: string }) => [item.key, item.value])
                )
            });
        },
        [config, setConfig]
    );

    const { watch, handleSubmit } = formMethods;
    useEffect(() => {
        const subscription = watch(handleSubmit(onChange) as any);
        return () => subscription.unsubscribe();
    }, [watch, onChange, handleSubmit]);

    const typeMode = watch('sourceType.mode');

    const columns =
        data?.metadata.columns.map((column) => ({
            value: column.name,
            label: column.name
        })) ?? [];

    return (
        <div className='flex flex-col flex-1 h-full min-w-0 min-h-0 pt-4'>
            <div className='pb-4 pl-6 pr-5 border-b border-dividerTertiary'>
                <StepTitleAndControls title='Map properties' />
            </div>

            <div className='flex flex-1 min-h-0'>
                <div className='flex flex-col flex-1 min-w-0 min-h-0 p-6 tile-scroll-overflow'>
                    <FormProvider {...formMethods}>
                        <form className='w-full' onSubmit={handleSubmit(onChange)}>
                            <Field label='ID Column' required={true}>
                                <EditorSelect
                                    name='sourceIdColumn'
                                    className='w-64'
                                    options={columns}
                                    placeholder='Select ID column'
                                />
                            </Field>
                            <Field label='Name Column' required={true}>
                                <EditorSelect
                                    name='sourceNameColumn'
                                    className='w-64'
                                    options={columns}
                                    placeholder='Select Name column'
                                />
                            </Field>

                            {/* One button inside is always disabled so we need to fix the text colour to avoid
                                it always being text-textDisabled */}
                            <Field label='Type' required={true} className='!text-textPrimary'>
                                <div className='flex flex-row gap-4'>
                                    <Field.Switch
                                        name='sourceType.mode'
                                        options={[
                                            { value: 'fixed', label: 'Fixed' },
                                            { value: 'column', label: 'Column' }
                                        ]}
                                    />

                                    {typeMode === 'fixed' ? (
                                        <div>
                                            <Field.Input
                                                name='sourceType.value'
                                                className='w-64'
                                                placeholder='Enter type'
                                            />
                                        </div>
                                    ) : (
                                        <EditorSelect
                                            name='sourceType.column'
                                            className='w-64'
                                            options={columns}
                                            placeholder='Select type column'
                                        />
                                    )}
                                </div>
                            </Field>

                            <Field label='Column Mapping'>
                                <div className='max-w-5xl'>
                                    <Field.Input
                                        type='key-value'
                                        name='columnMapping'
                                        keyInput={{
                                            placeholder: 'Property Name',
                                            type: 'autocomplete',
                                            options: columns,
                                            isMulti: false,
                                            selectOptionsAs: 'valueString'
                                        }}
                                        valueInput={{ placeholder: 'Column Name' }}
                                        verb='maps to'
                                        defaultKey=''
                                        defaultValue=''
                                        allowEncryption={false}
                                        emptyDefaultValue={true}
                                        className='w-full'
                                    />
                                </div>
                            </Field>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};
